import React, { useMemo, useEffect } from "react";
import {
  Controller,
  useFormContext,
  get,
  useFieldArray,
} from "react-hook-form";
import {
  Text,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Box,
  SimpleGrid,
  Switch,
  Input,
  Flex,
  Heading,
} from "@chakra-ui/react";
import { format } from "date-fns";
import { useTranslation } from "next-i18next";
import { useSession } from "next-auth/client";
import AnimateHeight from "react-animate-height";

import { AccreditationLocations } from "@/types/event";
import BoxCard from "@/components/common/BoxCard";
import SelectAdvanced from "@/components/common/SelectAdvanced";

const Location = ({
  index,
  location,
  rounded = true,
}: {
  index: number;
  location: AccreditationLocations;
  rounded?: boolean;
}): React.ReactElement => {
  const { t } = useTranslation("common");
  const [session] = useSession();
  const currentDate = new Date(Date.now());
  const startAccreditation = new Date(location.startAccreditation);
  const endAccreditation = new Date(location.endAccreditation);
  const isEndAccreditation = currentDate.getTime() > endAccreditation.getTime();

  const {
    formState: { errors },
    control,
    watch,
    register,
    setValue,
  } = useFormContext();

  const error = get(errors, `accreditationLocations.${index}`);

  const { fields } = useFieldArray({
    name: `accreditationLocations.${index}.accreditationOptions`,
    control,
  });

  const accreditationOptions = watch(
    `accreditationLocations.${index}.accreditationOptions`,
    fields
  );

  const accreditationTypes = watch(
    `accreditationLocations.${index}.accreditationTypes`
  );

  const locationHasFreeTypes = useMemo(
    () => accreditationTypes?.filter((type) => !type.limitReached),
    [accreditationTypes]
  );

  const accreditationTypeOptions = useMemo(
    () =>
      accreditationTypes
        ?.filter((type) => type.active || !type.default)
        .map((type) => ({
          label: type.limitReached
            ? `${
                type.default
                  ? t(`events.accreditationTypes.${type.name.en}`)
                  : type.name[session?.user?.profile?.locale] ??
                    type.name.en ??
                    type.name[Object.keys(type.name)[0]]
              } - ${t("events.accreditationTypeFullLimit")}`
            : type.default
            ? t(`events.accreditationTypes.${type.name.en}`)
            : type.name[session?.user?.profile?.locale] ??
              type.name.en ??
              type.name[Object.keys(type.name)[0]],
          value: type.id,
          disabled: type.limitReached,
        })),
    [accreditationTypes]
  );

  useEffect(() => {
    const oneType = accreditationTypes?.filter(
      (type) => type.active || !type.default
    );
    if (oneType?.length === 1)
      setValue(`accreditationLocations.${index}.choosedType`, oneType[0]?.id);
  }, []);

  if (
    accreditationTypes?.length === 0 ||
    isEndAccreditation ||
    locationHasFreeTypes?.length === 0
  )
    return null;

  return (
    <>
      <Heading size={"md"} mt={5} mb={2}>
        <Flex
          direction={{ base: "column", md: "row" }}
          alignItems={{ base: "flex-start", md: "center" }}
        >
          <Text>{location.location?.name || ""}</Text>
          <Text
            ml={{ base: 0, md: 3 }}
            mt={{ base: 3, md: 0 }}
            fontSize={{ base: "sm", md: "inherit" }}
            textAlign={{ base: "left", md: "center" }}
          >
            {t("events.accreditationAdmissionPeriod", {
              dateFrom: format(startAccreditation, "dd/MM/yyyy"),
              dateTo: format(endAccreditation, "dd/MM/yyyy"),
            })}
          </Text>
        </Flex>
      </Heading>
      <BoxCard
        p={0}
        flex={1}
        minHeight="100%"
        borderRadius={rounded ? ".375rem" : 0}
      >
        <Box p="1.25rem">
          <SimpleGrid spacingY={4}>
            <FormControl isInvalid={!!error?.choosedType} isRequired>
              <FormLabel>{t("events.accreditationType")}</FormLabel>
              <Controller
                control={control}
                name={`accreditationLocations.${index}.choosedType`}
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value, ref } }) => (
                  <SelectAdvanced
                    options={accreditationTypeOptions}
                    isOptionDisabled={(option) => option.disabled}
                    ref={ref}
                    onChange={(selectedValue) => onChange(selectedValue?.value)}
                    value={accreditationTypeOptions?.find(
                      (c) => value === c?.value
                    )}
                    isInvalid={!!error?.choosedType}
                  />
                )}
              />
              {error?.choosedType && (
                <FormErrorMessage>{t("users.fieldRequired")}</FormErrorMessage>
              )}
            </FormControl>
            {accreditationOptions?.length > 0 && (
              <>
                <Text fontWeight="bold">{t("events.additionalOptions")}</Text>
                {accreditationOptions?.map((option, fieldIndex) => (
                  <FormControl key={option.id}>
                    <Switch
                      {...register(
                        `accreditationLocations.${index}.accreditationOptions.${fieldIndex}.choosed`
                      )}
                      mr={2}
                    />
                    <FormLabel>
                      {option.name[session?.user?.profile?.locale] ??
                        option.name.en ??
                        option.name[Object.keys(option.name)[0]]}
                    </FormLabel>
                    <AnimateHeight
                      height={
                        accreditationOptions[fieldIndex]?.choosed &&
                        accreditationOptions[fieldIndex]?.description
                          ? "auto"
                          : 0
                      }
                    >
                      {accreditationOptions[fieldIndex]?.choosed &&
                        accreditationOptions[fieldIndex]?.description && (
                          <FormControl
                            isRequired={
                              accreditationOptions[fieldIndex]?.choosed
                            }
                            isInvalid={
                              !!error?.accreditationOptions[fieldIndex]
                            }
                          >
                            <FormLabel mb={2}>
                              {option.description[
                                session?.user?.profile?.locale
                              ] ??
                                option.description.en ??
                                option.description[
                                  Object.keys(option.description)[0]
                                ]}
                            </FormLabel>
                            <Input
                              {...register(
                                `accreditationLocations.${index}.accreditationOptions.${fieldIndex}.value`,
                                {
                                  required:
                                    accreditationOptions[fieldIndex]?.choosed,
                                }
                              )}
                            />
                            {error?.accreditationOptions[fieldIndex] && (
                              <FormErrorMessage>
                                {t("users.fieldRequired")}
                              </FormErrorMessage>
                            )}
                          </FormControl>
                        )}
                    </AnimateHeight>
                  </FormControl>
                ))}
              </>
            )}
          </SimpleGrid>
        </Box>
      </BoxCard>
    </>
  );
};

export default Location;
